import { ReactNode } from 'react';
import { cn } from '~/lib/cn';

import { motion } from 'framer-motion';
import Image from 'next/image';
import { Button } from '../../Button';

type FeatureRowProps = {
    header: ReactNode;
    imageSrc: string;
    imageAlt: string;
    ctaLink: string;
    ctaText: string;
    reverse?: boolean;
    children: ReactNode;
};
export const FeatureRow = ({
    header,
    imageSrc,
    imageAlt,
    ctaLink,
    ctaText,
    reverse = false,
    children
}: FeatureRowProps) => {
    return (
        <section className="tw-[overflow-anchor:none]">
            {header}

            <div className="tw-grid tw-grid-cols-1 tw-items-stretch tw-gap-8 lg:tw-grid-cols-2 lg:tw-gap-16">
                <motion.div
                    initial={{ filter: 'grayscale(100%)', opacity: 0, x: reverse ? 40 : -40 }}
                    whileInView={{ filter: 'grayscale(0%)', opacity: 1, x: 0 }}
                    viewport={{ amount: 0.3, once: true }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className={cn(
                        'tw-relative tw-h-[300px] tw-w-full tw-overflow-hidden tw-rounded-2xl lg:tw-h-[500px]',
                        reverse ? 'lg:tw-order-2' : 'lg:tw-order-1'
                    )}
                >
                    <Image
                        fill
                        src={imageSrc}
                        alt={imageAlt}
                        className="tw-card-bordered tw-object-cover tw-object-left-top tw-p-0 tw-transition-transform tw-duration-700"
                        // sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 650px"
                    />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: reverse ? -40 : 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ amount: 0.3, once: true }}
                    transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
                    className={cn(
                        'tw-card-bordered tw-group tw-relative tw-isolate tw-flex tw-flex-col tw-overflow-hidden tw-rounded-3xl tw-border tw-border-solid tw-border-accents-1 tw-bg-gradient-to-br tw-from-accents-1/50 tw-via-white tw-to-success/5 tw-p-6 tw-text-accents-7 tw-shadow-sm tw-transition-all tw-duration-200 lg:tw-p-10',
                        reverse ? 'lg:tw-order-1' : 'lg:tw-order-2'
                    )}
                >
                    <div
                        aria-hidden
                        className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-0 tw-bg-gradient-to-br tw-from-success/0 tw-via-success/5 tw-to-success/10 tw-opacity-0 tw-transition-opacity tw-duration-500 group-hover:tw-opacity-100"
                    />

                    <div className="tw-relative tw-z-10 tw-flex tw-h-full tw-flex-col tw-gap-4">
                        {children}

                        <div className="tw-mt-auto tw-pt-4">
                            <Button.Link title={ctaText} href={ctaLink}>
                                {ctaText}
                            </Button.Link>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
